"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";

import { Field, PageHeader, Panel, PrimaryButton, TextInput } from "@/components/ui";
import { ApiError, NetworkError, apiClient } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth/store";
import { bridge } from "@/lib/bridge";
import { useI18n } from "@/lib/i18n/provider";

type AuthVariant = "login" | "register" | "forgot-password" | "reset-password";

/** flows.json#/states — login/register are ready|submitting|error, the two
    password screens add submitted/complete and the invalid/expired code cases. */
type FormState =
  | "ready"
  | "submitting"
  | "error"
  | "submitted"
  | "complete"
  | "invalidCode"
  | "expiredCode";

const VARIANTS: Record<
  AuthVariant,
  { titleKey: string; submitKey: string; footerKey: string }
> = {
  login: {
    // Not "Sign In" — the submit button already says that, and repeating it as
    // the heading says nothing. This names what signing in gets you.
    titleKey: "home.loginPrompt.title",
    submitKey: "login.button.signIn",
    footerKey: "login.link.createOne",
  },
  register: {
    titleKey: "register.title",
    submitKey: "login.button.createAccount",
    footerKey: "login.link.haveAccount",
  },
  "forgot-password": {
    titleKey: "login.title.forgotPassword",
    submitKey: "login.button.sendResetCode",
    footerKey: "login.link.backToSignIn",
  },
  "reset-password": {
    titleKey: "resetPassword.title",
    submitKey: "login.button.resetPassword",
    footerKey: "login.link.backToSignIn",
  },
};

export function AuthPage({ variant }: { variant: AuthVariant }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const setToken = useAuthStore((state) => state.setToken);
  const { t, tf } = useI18n();

  const [form, setForm] = useState({
    email: searchParams.get("email") ?? "",
    password: "",
    firstName: "",
    lastName: "",
    code: "",
    newPassword: "",
  });
  const [state, setState] = useState<FormState>("ready");
  const [errorText, setErrorText] = useState<string | null>(null);

  const config = VARIANTS[variant];

  /** Only ever navigate to a same-origin path. flows.json#/flows/consumerAuthentication:
      "auth return targets are sanitized same-origin paths." */
  function safeNext() {
    const next = searchParams.get("next");
    if (!next || !next.startsWith("/") || next.startsWith("//")) return "/";
    return next;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");
    setErrorText(null);

    try {
      if (variant === "login") {
        const response = await apiClient.post<{ token: string; email: string }>(
          "/auth/signin",
          { email: form.email, password: form.password },
          "none",
        );
        setToken(response.token);
        router.push(safeNext());
        router.refresh();
        return;
      }

      if (variant === "register") {
        const response = await apiClient.post<{ token: string; email: string }>(
          "/auth/register",
          {
            email: form.email,
            password: form.password,
            firstName: form.firstName,
            lastName: form.lastName,
          },
          "none",
        );
        setToken(response.token);
        router.push("/");
        router.refresh();
        return;
      }

      if (variant === "forgot-password") {
        // The response is deliberately enumeration-safe, so we render our own
        // confirmation from the contract rather than echoing the server's.
        await apiClient.post("/auth/forgot-password", { email: form.email }, "none");
        setState("submitted");
        return;
      }

      await apiClient.post(
        "/auth/reset-password",
        { email: form.email, code: form.code, newPassword: form.newPassword },
        "none",
      );
      setState("complete");
      router.push(`/login?email=${encodeURIComponent(form.email)}`);
    } catch (error) {
      if (error instanceof NetworkError) {
        setState("error");
        setErrorText(t("web.connection.offline"));
        return;
      }
      if (variant === "reset-password" && error instanceof ApiError) {
        // managua distinguishes a wrong code from an aged-out one; flows.json
        // gives resetPassword a distinct state for each so the visitor knows
        // whether to retype or to request a new code.
        const expired = /expired/i.test(error.message);
        setState(expired ? "expiredCode" : "invalidCode");
        return;
      }
      bridge.reportError(`${variant} failed: ${String(error)}`, "login");
      setState("error");
      setErrorText(t("common.error"));
    }
  }

  const statusMessage = (() => {
    switch (state) {
      case "submitted":
        return form.email
          ? tf("login.forgotPassword.sent", { email: form.email })
          : t("forgotPassword.sent");
      case "complete":
        return t("resetPassword.complete");
      case "invalidCode":
        return t("resetPassword.invalidCode");
      case "expiredCode":
        return t("resetPassword.expiredCode");
      case "error":
        return errorText;
      default:
        return null;
    }
  })();

  const isFailure = state === "error" || state === "invalidCode" || state === "expiredCode";

  return (
    <div className="tk-stack tk-stack-lg">
      <PageHeader
        eyebrowKey="login.section.account"
        titleKey={config.titleKey}
        // Sign-in is reached from the dock or a gated page: back returns there.
        back={{ href: variant === "login" ? safeNext() : "/login" }}
      />

      <LegacySessionNotice />

      <Panel className="tk-measure">
        <form className="tk-stack" onSubmit={handleSubmit}>
          <Field labelKey="link.email.label">
            {(id) => (
              <TextInput
                id={id}
                type="email"
                autoComplete="email"
                placeholder={t("login.field.email.placeholder")}
                value={form.email}
                onChange={(event) =>
                  setForm((current) => ({ ...current, email: event.target.value }))
                }
                required
              />
            )}
          </Field>

          {variant === "register" ? (
            <div className="tk-grid-2">
              <Field labelKey="login.field.firstName">
                {(id) => (
                  <TextInput
                    id={id}
                    autoComplete="given-name"
                    value={form.firstName}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, firstName: event.target.value }))
                    }
                  />
                )}
              </Field>
              <Field labelKey="login.field.lastName">
                {(id) => (
                  <TextInput
                    id={id}
                    autoComplete="family-name"
                    value={form.lastName}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, lastName: event.target.value }))
                    }
                  />
                )}
              </Field>
            </div>
          ) : null}

          {variant === "login" || variant === "register" ? (
            <Field labelKey="link.password.label">
              {(id) => (
                <TextInput
                  id={id}
                  type="password"
                  autoComplete={variant === "login" ? "current-password" : "new-password"}
                  placeholder={t("login.field.password.placeholder")}
                  value={form.password}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, password: event.target.value }))
                  }
                  required
                />
              )}
            </Field>
          ) : null}

          {variant === "forgot-password" ? (
            <p className="tk-caption tk-secondary">{t("login.forgot.body")}</p>
          ) : null}

          {variant === "reset-password" ? (
            <>
              <p className="tk-caption tk-secondary">{t("login.help.enterCode")}</p>
              <Field labelKey="login.field.code.placeholder">
                {(id) => (
                  <TextInput
                    id={id}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={form.code}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, code: event.target.value }))
                    }
                    required
                  />
                )}
              </Field>
              <Field labelKey="login.field.newPassword.placeholder">
                {(id) => (
                  <TextInput
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={form.newPassword}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, newPassword: event.target.value }))
                    }
                    required
                  />
                )}
              </Field>
            </>
          ) : null}

          {statusMessage ? (
            <p
              className={`tk-banner ${isFailure ? "tk-banner-warning" : "tk-banner-info"}`}
              role={isFailure ? "alert" : "status"}
            >
              <span aria-hidden="true">{isFailure ? "!" : "✓"}</span>
              {statusMessage}
            </p>
          ) : null}

          <div className="tk-row tk-row-between">
            <p className="tk-caption tk-secondary tk-truncate">{t(config.footerKey)}</p>
            <PrimaryButton
              type="submit"
              busy={state === "submitting"}
              labelKey={config.submitKey}
            />
          </div>
        </form>

        <div className="tk-row">
          {variant !== "login" ? (
            <Link className="tk-link" href="/login">
              {t("login.link.backToSignIn")}
            </Link>
          ) : null}
          {variant !== "register" ? (
            <Link className="tk-link" href="/register">
              {t("login.button.createAccount")}
            </Link>
          ) : null}
          {variant !== "forgot-password" ? (
            <Link className="tk-link" href="/forgot-password">
              {t("login.link.forgotPassword")}
            </Link>
          ) : null}
        </div>
      </Panel>
    </div>
  );
}

/**
 * flows.json#/flows/webLegacySessionCutover, the visible half.
 *
 * A returning visitor arrives holding the legacy portal's `appToken`, which
 * managua cannot use. The store has already discarded it and parked the session
 * in `legacySession`; SessionGate routed here. All that is left is to say so.
 *
 * There is deliberately no "your password has changed" language, because it
 * hasn't: both backends read the same MySQL terra_accounts and managua
 * normalizes PHP's bcrypt $2y$ prefix, so the password they already have works.
 * That is why there is no reset campaign attached to the cutover.
 *
 * Delete this component once the legacy key has aged out of the population.
 */
export function LegacySessionNotice() {
  const sessionState = useAuthStore((state) => state.sessionState);
  const { t } = useI18n();

  if (sessionState !== "legacySession") return null;

  return (
    <p className="tk-banner tk-banner-info" role="status">
      <span aria-hidden="true">↻</span>
      {t("web.session.legacyNotice")}
    </p>
  );
}
